import { useEffect, useId, useState } from 'react';
import { useT } from '../../i18n';
import { api } from '../../lib/api';

interface FilePreviewModalProps {
  filePath: string | null;
  onClose: () => void;
}

type PreviewStatus = 'loading' | 'ready' | 'error' | 'unsupported';

export default function FilePreviewModal({ filePath, onClose }: FilePreviewModalProps) {
  const t = useT();
  const titleId = useId();
  const [status, setStatus] = useState<PreviewStatus>('loading');
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!filePath) return;

    let cancelled = false;
    let nextUrl: string | null = null;
    setStatus('loading');
    setObjectUrl(null);
    setMimeType('');
    setError('');

    api.healthRecords
      .file(filePath)
      .then((blob) => {
        if (cancelled) return;
        nextUrl = URL.createObjectURL(blob);
        setObjectUrl(nextUrl);
        setMimeType(blob.type);
        setStatus(blob.type.startsWith('image/') || blob.type === 'application/pdf' ? 'ready' : 'unsupported');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setStatus('error');
      });

    return () => {
      cancelled = true;
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [filePath]);

  useEffect(() => {
    if (!filePath) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filePath, onClose]);

  if (!filePath) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-cocoa/60 p-4 backdrop-blur-sm" role="presentation" onMouseDown={onClose}>
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-card"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-coffee/70">
              {t('health.preview.kicker')}
            </p>
            <h3 id={titleId} className="mt-1 text-lg font-semibold text-cocoa">
              {t('health.preview.title')}
            </h3>
            <p className="mt-1 break-all font-mono text-xs text-coffee/70">{filePath}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1.5 font-mono text-xs text-coffee transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
          >
            {t('health.preview.close')}
          </button>
        </header>

        <div className="min-h-[50vh] overflow-auto bg-neutral-50 p-4">
          {status === 'loading' && <p className="p-6 text-coffee">{t('health.preview.loading')}</p>}
          {status === 'error' && (
            <p className="p-6 font-mono text-sm italic text-cocoa">
              {t('health.preview.error', { error })}
            </p>
          )}
          {status === 'unsupported' && <p className="p-6 text-coffee">{t('health.preview.unsupported')}</p>}
          {status === 'ready' && objectUrl && mimeType.startsWith('image/') && (
            <img src={objectUrl} alt={filePath} className="mx-auto max-h-[75vh] max-w-full rounded-lg bg-white object-contain shadow-soft" />
          )}
          {status === 'ready' && objectUrl && mimeType === 'application/pdf' && (
            <iframe src={objectUrl} title={filePath} className="h-[75vh] w-full rounded-lg border border-neutral-200 bg-white" />
          )}
        </div>
      </section>
    </div>
  );
}
